export type SignatureDetails = {
  name: string;
  role: string;
  email: string;
  phone: string;
  website: string;
  showLogo: boolean;
};

const LOGO_URL = "https://bookburrow.uk/apple-touch-icon.png";

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}

function websiteUrl(value: string): URL | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
}

export function isValidSignatureEmail(value: string): boolean {
  return /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value.trim());
}

export function buildEmailSignatureHtml(details: SignatureDetails): string {
  const name = escapeHtml(details.name.trim() || "Your name");
  const role = escapeHtml(details.role.trim());
  const email = details.email.trim();
  const safeEmail = isValidSignatureEmail(email) ? email : "";
  const phone = details.phone.trim();
  const telephone = phone.replace(/[^\d+]/g, "");
  const website = websiteUrl(details.website);
  const websiteLabel = website
    ? escapeHtml(website.hostname.replace(/^www\./, "") + (website.pathname === "/" ? "" : website.pathname))
    : "";

  const contactItems = [
    safeEmail
      ? `<a href="mailto:${encodeURIComponent(safeEmail)}" style="color:#194320;text-decoration:none;">${escapeHtml(safeEmail)}</a>`
      : "",
    phone && telephone
      ? `<a href="tel:${escapeHtml(telephone)}" style="color:#194320;text-decoration:none;">${escapeHtml(phone)}</a>`
      : "",
    website
      ? `<a href="${escapeHtml(website.href)}" style="color:#194320;text-decoration:none;">${websiteLabel}</a>`
      : "",
  ].filter(Boolean);

  const contacts = contactItems.length
    ? `<p style="margin:8px 0 0;font:13px/1.6 Arial,Helvetica,sans-serif;">${contactItems.join('<span style="color:#b7a987;"> &nbsp;·&nbsp; </span>')}</p>`
    : "";

  const logoCell = details.showLogo
    ? `<td style="padding:0 16px 0 0;vertical-align:top;"><img src="${LOGO_URL}" alt="Bookburrow" width="54" height="54" style="display:block;width:54px;height:54px;border:0;border-radius:10px;" /></td>`
    : "";

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;max-width:540px;"><tbody><tr>${logoCell}<td style="padding:0 0 0 16px;border-left:3px solid #caa85f;vertical-align:top;"><p style="margin:0;color:#194320;font:600 19px/1.25 Georgia,'Times New Roman',serif;">${name}</p><p style="margin:3px 0 0;color:#665b4f;font:13px/1.4 Arial,Helvetica,sans-serif;">${role ? `${role} <span style="color:#b7a987;">·</span> ` : ""}Bookburrow</p>${contacts}<p style="margin:11px 0 0;color:#68746a;font:italic 12px/1.4 Georgia,'Times New Roman',serif;">A little order. A lot more reading.</p></td></tr></tbody></table>`;
}

export function buildEmailSignatureText(details: SignatureDetails): string {
  return [
    details.name.trim(),
    [details.role.trim(), "Bookburrow"].filter(Boolean).join(" · "),
    details.email.trim(),
    details.phone.trim(),
    details.website.trim(),
    "A little order. A lot more reading.",
  ].filter(Boolean).join("\n");
}
